import type { TDocumentDefinitions } from 'pdfmake/interfaces';
import { loadPdfImage, resolveAppAssetUrl } from '../appAssets';
import {
    buildResponsePdfDefinition,
    collectResponsePdfImagePaths,
    getResponsePdfTitle,
    ResponsePdfImages,
    ResponsePdfInput,
} from './responsePdfDocument';
import { buildResponsePdfFileName } from './pdfFileName';

type PdfMake = typeof import('pdfmake/build/pdfmake');

let pdfMakePromise: Promise<{ pdfMake: PdfMake; vfs: Record<string, string> }> | undefined;

// pdfmake and its fonts are about 2 MB, so they are only loaded when a PDF is created.
const loadPdfMake = (): Promise<{ pdfMake: PdfMake; vfs: Record<string, string> }> => {
    if (!pdfMakePromise) {
        pdfMakePromise = Promise.all([import('pdfmake/build/pdfmake'), import('pdfmake/build/vfs_fonts')]).then(
            ([pdfMakeModule, fontsModule]) => {
                const pdfMake = ((pdfMakeModule as { default?: PdfMake }).default ?? pdfMakeModule) as PdfMake;
                const fonts = ((fontsModule as { default?: unknown }).default ?? fontsModule) as {
                    pdfMake?: { vfs: Record<string, string> };
                    vfs?: Record<string, string>;
                } & Record<string, string>;
                // pdfmake 0.2.x builds export the font files either directly or nested in pdfMake.vfs.
                return { pdfMake, vfs: fonts.pdfMake?.vfs ?? fonts.vfs ?? fonts };
            },
        );
        pdfMakePromise.catch(() => {
            pdfMakePromise = undefined;
        });
    }
    return pdfMakePromise;
};

export const renderPdfBlob = async (definition: TDocumentDefinitions): Promise<Blob> => {
    const { pdfMake, vfs } = await loadPdfMake();
    return new Promise((resolve, reject) => {
        try {
            pdfMake.createPdf(definition, undefined, undefined, vfs).getBlob(resolve);
        } catch (error) {
            reject(error);
        }
    });
};

export const renderPdfBytes = async (definition: TDocumentDefinitions): Promise<Uint8Array> => {
    const { pdfMake, vfs } = await loadPdfMake();
    return new Promise((resolve, reject) => {
        try {
            pdfMake.createPdf(definition, undefined, undefined, vfs).getBuffer((buffer) => resolve(buffer));
        } catch (error) {
            reject(error);
        }
    });
};

export const loadResponsePdfImages = async (
    paths: string[],
    cache: ResponsePdfImages = new Map(),
): Promise<ResponsePdfImages> => {
    await Promise.all(
        paths
            .filter((path) => !cache.has(path))
            .map(async (path) => {
                const url = resolveAppAssetUrl(path);
                cache.set(path, url ? await loadPdfImage(url) : null);
            }),
    );
    return cache;
};

export const createResponsePdf = async (
    input: ResponsePdfInput,
    imageCache?: ResponsePdfImages,
): Promise<{ blob: Blob; filename: string }> => {
    const images = await loadResponsePdfImages(collectResponsePdfImagePaths(input), imageCache);
    const blob = await renderPdfBlob(buildResponsePdfDefinition(input, images));

    return {
        blob,
        filename: buildResponsePdfFileName({
            task: input.task,
            questionnaireResponse: input.questionnaireResponse,
            title: getResponsePdfTitle(input),
        }),
    };
};
